import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import client from "@/lib/api";
import { logger } from "@/lib/logger";

export default function SamplerCaptureModal({ open, onClose, prefEmail }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState(prefEmail || "");
  const [status, setStatus] = useState("form"); // form → success
  const [message, setMessage] = useState("");
  const [downloads, setDownloads] = useState({ print: null, mobile: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => { setEmail(prefEmail || ""); }, [prefEmail]);
  useEffect(() => {
    if (!open) {
      setStatus("form"); setMessage(""); setLoading(false);
      setFirstName(""); setDownloads({ print: null, mobile: null });
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await client.post("/email/capture", {
        email,
        first_name: firstName,
        source: "sampler",
      });
      setDownloads({ print: data.print_url, mobile: data.mobile_url });
      setMessage(data.message);
      setStatus("success");
    } catch (err) {
      logger.error("Sampler capture failed:", err);
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="sampler-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-mizizi-charcoal/70 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md bg-mizizi-cream text-mizizi-charcoal shadow-2xl border border-mizizi-gold/40">
        <button
          type="button"
          onClick={onClose}
          data-testid="sampler-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full hover:bg-mizizi-charcoal/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 md:p-10">
          <div className="overline text-mizizi-terracotta">Free · Digital sampler</div>
          <h3 className="font-serif text-3xl mt-3 leading-tight">A Taste of Mizizi</h3>
          <p className="mt-3 text-sm text-mizizi-charcoal/70 leading-relaxed">
            Five signature recipes — beautifully typeset, delivered directly to your inbox.
          </p>

          {status === "form" && (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="overline text-mizizi-charcoal/70 block mb-1" htmlFor="sampler-name">First name</label>
                <input
                  id="sampler-name" required minLength={1} value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="sampler-first-name"
                  className="w-full px-4 py-3 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta"
                />
              </div>
              <div>
                <label className="overline text-mizizi-charcoal/70 block mb-1" htmlFor="sampler-email">Email address</label>
                <input
                  id="sampler-email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="sampler-email"
                  className="w-full px-4 py-3 bg-transparent border border-mizizi-gold/50 focus:outline-none focus:border-mizizi-terracotta"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="sampler-submit"
                className="w-full pill pill-primary justify-center disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send my free sampler"}
              </button>
              <p className="text-[11px] text-mizizi-charcoal/50 text-center pt-2">
                Complimentary. No card required. Unsubscribe anytime.
              </p>
            </form>
          )}

          {status === "success" && (
            <div className="mt-8 text-center" data-testid="sampler-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-mizizi-gold/20 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-mizizi-gold" />
              </div>
              <p className="mt-6 font-serif text-2xl">Karibu, {firstName || "friend"}.</p>
              <p className="mt-2 text-sm text-mizizi-charcoal/70 leading-relaxed">{message}</p>
              {(downloads.print || downloads.mobile) && (
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  {downloads.print && (
                    <a
                      href={downloads.print}
                      data-testid="sampler-download-print"
                      className="pill pill-primary"
                    >
                      Download PDF
                    </a>
                  )}
                  {downloads.mobile && (
                    <a
                      href={downloads.mobile}
                      data-testid="sampler-download-mobile"
                      className="pill pill-outline"
                    >
                      Mobile version
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
